// Libaries
import { omtApi } from 'config/axios';

import { gcPlusSettingsRoleKey } from 'models/preferences/PreferencesMapping';
import { getMockSubscriptions } from './mockSubscriptions';
import { subscription, subscriptions } from './subscriptionFields';
import { useUserHasFeature } from './userPreferenceData';

// Util
import { OMTSessionUseSpecificQuery, GetOMTHeaders } from 'services/omt/omtUtils';

// Base TanStack types
/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').UndefinedSpecificInitialDataOptions<TData>} UndefinedSpecificInitialDataOptions
 */

/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').DefinedSpecificInitialDataOptions<TData>} DefinedSpecificInitialDataOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseQueryResult<TData, TError>} UseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseQueryResult<TData, TError>} DefinedUseQueryResult
 */

// Custom types
/**
 * @typedef {import('utils/gcPlusSubscription/gcPlusSubscription').CustomerSubscriptionResponse} CustomerSubscriptionResponse
 */

/**
 * Check if user has subscription.
 * Returns mock subscription if user has no DI_TESTER role.
 *
 * @param {string} customerGuid Customer GUID
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @returns {UseQueryResult<CustomerSubscriptionResponse> | DefinedUseQueryResult<CustomerSubscriptionResponse>}
 */
export const useUserSubscription = (customerGuid, queryParams = { enabled: true }) => {
    const { isPending, isAvailable: subscriptionFeatureAvailable } = useUserHasFeature(gcPlusSettingsRoleKey);

    return OMTSessionUseSpecificQuery({
        staleTime: 3.6e6, // 1 hour
        ...(queryParams || {}),
        queryKey: [subscription, customerGuid],
        queryFn: () => {
            if (subscriptionFeatureAvailable) {
                return omtApi
                    .get(`/api/${subscription}/customers/${customerGuid}/${subscriptions}`, {
                        headers: GetOMTHeaders()
                    })
                    .then((res) => res.data);
            }
        },
        enabled: !!(
            !!customerGuid &&
            (queryParams && queryParams.enabled !== undefined
                ? !!queryParams.enabled && !isPending && subscriptionFeatureAvailable
                : !isPending && subscriptionFeatureAvailable)
        )
    });
};
